import { useCallback, useEffect } from 'react'
import GeoJSON from 'geojson'
import { Expression, ExpressionSpecification, SymbolLayerSpecification } from 'mapbox-gl'

import { stackIds } from '../../containers/MapStackPresenter'
import { USER_GEOMETRY_LAYER_ID_PREFIX } from '../MapView/constants'
import { useMapContext } from '../MapView/MapContext'
import { MapGeoJsonLayer } from '../MapView/MapGeoJsonLayer'
import { MapGeoJsonSource } from '../MapView/MapGeoJsonSource'
import { MapSymbolLayer } from '../MapView/MapSymbolLayer'
import { hasLiveStyle } from '../MapView/mapTeardown'
import {
  RENDER_COLOR_KEY,
  RENDER_FILL_OPACITY_KEY,
  RENDER_HAS_LABEL_KEY,
  RENDER_LABEL_KEY,
  RENDER_LABEL_MIN_ZOOM_KEY,
  RENDER_LINE_STYLE_KEY,
  RENDER_LINE_WIDTH_KEY,
  RENDER_POINT_ICON_KEY,
  RENDER_STROKE_OPACITY_KEY,
  UserGeometryRenderData
} from './userGeometryRenderData'
import { DEFAULT_LABEL_MIN_ZOOM, DEFAULT_POINT_LABEL_MIN_ZOOM } from './userGeometryRenderResolvers'

import { pinImageDataUri, pinImageName } from '@/presentation/components/ColorCarousel'

const LABEL_TEXT_COLOR = '#FCFCFC'
const LABEL_HALO_COLOR = '#0D1012'
const LABEL_FONT = ['Open Sans Bold']
const LABEL_SIZE = 12

// Dasharray patterns (in line-width units) for each stored line-style. Mapbox
// cannot data-drive `line-dasharray`, so line features are split per style and
// each style gets its own layer with a constant dasharray.
const DASHED_PATTERN = [2, 2]
const DOTTED_PATTERN = [0.4, 1.6]

// Only label features flagged with a non-empty name + showLabel. This is a pure
// data filter (no `["zoom"]`, which is invalid in Mapbox filters).
const HAS_LABEL_FILTER = ['==', ['get', RENDER_HAS_LABEL_KEY], true]

// Static layout/paint kept at module scope so their identity is stable —
// MapSymbolLayer re-adds its layer whenever layout/paint change identity.
const POLYGON_LABEL_LAYOUT: SymbolLayerSpecification['layout'] = {
  'text-field': ['get', RENDER_LABEL_KEY],
  'text-size': LABEL_SIZE,
  'text-font': LABEL_FONT,
  'text-anchor': 'center',
  'text-allow-overlap': false,
  'text-optional': true
}

const POINT_LABEL_LAYOUT: SymbolLayerSpecification['layout'] = {
  'text-field': ['get', RENDER_LABEL_KEY],
  'text-size': LABEL_SIZE,
  'text-font': LABEL_FONT,
  'text-anchor': 'top',
  'text-offset': [0, 0.8],
  'text-allow-overlap': false,
  'text-optional': true
}

const LABEL_PAINT: SymbolLayerSpecification['paint'] = {
  'text-color': LABEL_TEXT_COLOR,
  'text-halo-color': LABEL_HALO_COLOR,
  'text-halo-width': 1.5
}

// Pin marker layout: the tip sits on the coordinate (`icon-anchor: bottom`) and
// it always shows (markers should not declutter like labels).
const POINT_PIN_LAYOUT: SymbolLayerSpecification['layout'] = {
  'icon-anchor': 'bottom',
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 10, 1, 16, 1.3]
}

const POINT_ICON_EXPRESSION = ['get', RENDER_POINT_ICON_KEY] as ExpressionSpecification

interface MapGeometriesProps {
  // Geometry id, used to namespace this geometry's sources/layers.
  geometryId: string
  geometryName: string
  renderData: UserGeometryRenderData
  onFeatureClick?: (feature: GeoJSON.Feature, geometryName: string) => void
}

// Smallest per-feature `labelMinZoom` across a collection, used as the symbol
// layer's `minzoom`. Per-feature zoom thresholds cannot be expressed in a
// single Mapbox layer (`["zoom"]` is invalid in filters and may only be a
// top-level step/interpolate input), so we gate the whole layer at the earliest
// threshold and let symbol collision (`text-allow-overlap: false`) declutter
// the rest.
const minLabelZoom = (features: GeoJSON.Feature[], fallback: number): number => {
  let min = Infinity
  features.forEach((feature) => {
    if (feature.properties?.[RENDER_HAS_LABEL_KEY] !== true) return
    const zoom = feature.properties?.[RENDER_LABEL_MIN_ZOOM_KEY]
    if (typeof zoom === 'number' && zoom < min) min = zoom
  })
  return Number.isFinite(min) ? min : fallback
}

function MapGeometries(props: MapGeometriesProps) {
  const { geometryId, geometryName, renderData, onFeatureClick } = props

  const { map } = useMapContext()

  const handleFeatureClick = useCallback(
    (features: GeoJSON.Feature[]) => {
      const feature = features?.[0]
      if (feature) onFeatureClick?.(feature, geometryName)
    },
    [onFeatureClick, geometryName]
  )

  // Register a pin image (tinted by colour) for every colour present among the
  // points, so the symbol layer's `icon-image` lookup resolves. Images persist
  // on the map style and are reused; new colours (e.g. live edits) are added.
  useEffect(() => {
    if (!map) return
    const colors = new Set<string>()
    renderData.points.features.forEach((feature) => {
      const color = feature.properties?.[RENDER_COLOR_KEY]
      if (typeof color === 'string') colors.add(color)
    })
    colors.forEach((color) => {
      const name = pinImageName(color)
      if (map.hasImage(name)) return
      const image = new Image()
      image.onload = () => {
        // The style may have been reloaded while the image was decoding.
        if (hasLiveStyle(map) && !map.hasImage(name)) map.addImage(name, image, { pixelRatio: 2 })
      }
      image.src = pinImageDataUri(color)
    })
  }, [map, renderData.points])

  const baseId = `${USER_GEOMETRY_LAYER_ID_PREFIX}${geometryId}`

  const lineStyles: { style: 'solid' | 'dashed' | 'dotted'; data: GeoJSON.FeatureCollection }[] = [
    { style: 'solid', data: renderData.lines.solid },
    { style: 'dashed', data: renderData.lines.dashed },
    { style: 'dotted', data: renderData.lines.dotted }
  ]

  return (
    <>
      {/* Polygons: a single fill, then one outline layer per line-style (so the
          border honours the dash pattern — `line-dasharray` cannot be
          data-driven), then a centroid label. */}
      {renderData.polygons.features.length > 0 && (
        <MapGeoJsonSource id={`${baseId}-polygon-source`} data={renderData.polygons}>
          <MapGeoJsonLayer
            id={`${baseId}-polygon`}
            sourceId={`${baseId}-polygon-source`}
            types={['fill']}
            beforeLayerId={stackIds.userGeometries.polygon}
            fillColor={['get', RENDER_COLOR_KEY] as Expression}
            fillOpacity={['get', RENDER_FILL_OPACITY_KEY] as Expression}
            // Fill covers the whole polygon (clickable even when fill-opacity is
            // 0); registering the outline too would double-fire on edge clicks.
            onFillClick={handleFeatureClick}
          />
          {lineStyles.map(({ style }) => (
            <MapGeoJsonLayer
              key={style}
              id={`${baseId}-polygon-border-${style}`}
              sourceId={`${baseId}-polygon-source`}
              types={['line']}
              beforeLayerId={stackIds.userGeometries.polygon}
              filter={['==', ['get', RENDER_LINE_STYLE_KEY], style]}
              borderColor={['get', RENDER_COLOR_KEY] as Expression}
              borderWidth={['get', RENDER_LINE_WIDTH_KEY] as Expression}
              borderOpacity={['get', RENDER_STROKE_OPACITY_KEY] as Expression}
              layout={{ 'line-cap': style === 'dotted' ? 'round' : 'butt', 'line-join': 'round' }}
              paint={
                style === 'dashed'
                  ? { 'line-dasharray': DASHED_PATTERN }
                  : style === 'dotted'
                    ? { 'line-dasharray': DOTTED_PATTERN }
                    : {}
              }
            />
          ))}
          <MapSymbolLayer
            id={`${baseId}-polygon-label`}
            sourceId={`${baseId}-polygon-source`}
            beforeLayerId={stackIds.userGeometries.polygonLabel}
            minZoomLevel={minLabelZoom(renderData.polygons.features, DEFAULT_LABEL_MIN_ZOOM)}
            filter={HAS_LABEL_FILTER}
            textColor={LABEL_TEXT_COLOR}
            layout={POLYGON_LABEL_LAYOUT}
            paint={LABEL_PAINT}
          />
        </MapGeoJsonSource>
      )}

      {/* Lines: one layer per line-style (dasharray cannot be data-driven). */}
      {lineStyles.map(({ style, data }) =>
        data.features.length > 0 ? (
          <MapGeoJsonSource key={style} id={`${baseId}-line-${style}-source`} data={data}>
            <MapGeoJsonLayer
              id={`${baseId}-line-${style}`}
              sourceId={`${baseId}-line-${style}-source`}
              types={['line']}
              beforeLayerId={stackIds.userGeometries.line}
              borderColor={['get', RENDER_COLOR_KEY] as Expression}
              borderWidth={['get', RENDER_LINE_WIDTH_KEY] as Expression}
              borderOpacity={['get', RENDER_STROKE_OPACITY_KEY] as Expression}
              layout={{ 'line-cap': style === 'dotted' ? 'round' : 'butt', 'line-join': 'round' }}
              paint={
                style === 'dashed'
                  ? { 'line-dasharray': DASHED_PATTERN }
                  : style === 'dotted'
                    ? { 'line-dasharray': DOTTED_PATTERN }
                    : {}
              }
              onBorderClick={handleFeatureClick}
            />
          </MapGeoJsonSource>
        ) : null
      )}

      {/* Points: the chosen pin icon (tinted by colour), plus a label. */}
      {renderData.points.features.length > 0 && (
        <MapGeoJsonSource id={`${baseId}-point-source`} data={renderData.points}>
          <MapSymbolLayer
            id={`${baseId}-point`}
            sourceId={`${baseId}-point-source`}
            beforeLayerId={stackIds.userGeometries.point}
            icon={POINT_ICON_EXPRESSION}
            layout={POINT_PIN_LAYOUT}
            onSymbolClick={(event) => handleFeatureClick((event.features ?? []) as GeoJSON.Feature[])}
          />
          <MapSymbolLayer
            id={`${baseId}-point-label`}
            sourceId={`${baseId}-point-source`}
            beforeLayerId={stackIds.userGeometries.pointLabel}
            minZoomLevel={minLabelZoom(renderData.points.features, DEFAULT_POINT_LABEL_MIN_ZOOM)}
            filter={HAS_LABEL_FILTER}
            textColor={LABEL_TEXT_COLOR}
            layout={POINT_LABEL_LAYOUT}
            paint={LABEL_PAINT}
          />
        </MapGeoJsonSource>
      )}
    </>
  )
}

export { MapGeometries }
